import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import api from "../lib/api";
import { formatDay, minorToText, todayIso, toMinor } from "../lib/finance";
import { PERIODICITY_LABELS, addDays, billingError, listPrice, money, monthlyMinor, needsPriceReason, newKey, priceText, trialLength } from "../lib/billing";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Line({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-900 tabular-nums">{children}</span>
    </div>
  );
}

const ACCOUNT_STATUS = { pre_activation: "pas encore en service", active: "en service", suspended: "suspendu" };

// A pharmacy with a customer account that is not closed (GET /billing/pharmacy-options): no customers right needed.
function PharmacyField({ value, onChange }) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/billing/pharmacy-options", { params: search ? { search } : {} });
        setItems(data.items);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  if (value) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2" data-testid="billing-form-pharmacy">
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{value.pharmacy_name}</div>
          <div className="text-xs text-slate-500">Dossier client {ACCOUNT_STATUS[value.account_status] || ""}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>Changer</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une pharmacie cliente…" className="pl-9 h-10"
          aria-label="Rechercher une pharmacie cliente" data-testid="billing-form-pharmacy-search" />
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
        {loading && <p className="p-3 text-sm text-slate-500">Recherche…</p>}
        {!loading && items.length === 0 && <p className="p-3 text-sm text-slate-500">Aucune pharmacie cliente ne correspond. Un abonnement suit la conversion d'une pharmacie (dossier client).</p>}
        {!loading && items.map((p) => (
          <button key={p.prospect_id} type="button" disabled={p.open_subscription} onClick={() => onChange(p)}
            className="w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed" data-testid={`billing-form-pharmacy-${p.prospect_id}`}>
            <div className="text-sm font-medium text-slate-900">{p.pharmacy_name}{p.commune ? <span className="font-normal text-slate-500"> · {p.commune}</span> : null}</div>
            <div className="text-xs text-slate-500">{p.open_subscription ? "A déjà un abonnement en cours ou en brouillon" : `Dossier client ${ACCOUNT_STATUS[p.account_status] || ""}`}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function BillingSubscriptionForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const key = useRef(newKey());
  const [pharmacy, setPharmacy] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offerId, setOfferId] = useState("");
  const [periodicity, setPeriodicity] = useState("monthly");
  const [price, setPrice] = useState("");
  const [priceReason, setPriceReason] = useState("");
  const [start, setStart] = useState(todayIso());
  const [trial, setTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/billing/offers", { params: { include_archived: false } }).then(({ data }) => {
      const sellable = data.items.filter((o) => o.published);
      setOffers(sellable);
      if (sellable[0]) setOfferId(sellable[0].id);
    }).catch(() => setOffers([]));
    const prospect = params.get("prospect");
    if (prospect) {
      api.get("/billing/pharmacy-options").then(({ data }) => {
        const found = data.items.find((p) => p.prospect_id === prospect && !p.open_subscription);
        if (found) setPharmacy(found);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const offer = offers.find((o) => o.id === offerId);
  const version = offer?.published;
  const currency = version?.content.currency || "XOF";
  // The offer's first periodicity and its list price, each time another offer is picked.
  useEffect(() => {
    if (!version) return;
    const first = version.content.periods[0]?.periodicity || "monthly";
    setPeriodicity(first);
    const lp = listPrice(version, first);
    setPrice(lp == null ? "" : minorToText(lp, currency));
    setPriceReason("");
    setTrial(!!version.content.trial_days);
  }, [offerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (trial && version?.content.trial_days && start) setTrialEnd(addDays(start, version.content.trial_days - 1));
  }, [trial, start, offerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const trialDays = trial ? trialLength(start, trialEnd) : null;
  const priceMinor = toMinor(price, currency);
  const reasonNeeded = useMemo(() => needsPriceReason(version, periodicity, price), [version, periodicity, price]);
  const onQuote = listPrice(version, periodicity) == null;
  const valid = pharmacy && offer && priceMinor !== null && start && (!reasonNeeded || priceReason.trim().length >= 3) && (!trial || trialDays);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/billing/subscriptions", {
        prospect_id: pharmacy.prospect_id, offer_id: offerId, periodicity, price, ...(reasonNeeded ? { price_reason: priceReason.trim() } : {}),
        start_date: start, trial_end_date: trial ? trialEnd : null, note,
      }, { headers: { "Idempotency-Key": key.current } });
      navigate(`/revenus/abonnements/${data.id}`);
    } catch (e) {
      setError(billingError(e, "Création impossible. Réessayez."));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="billing-subscription-form">
      <Link to="/revenus/abonnements" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Abonnements clients</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouvel abonnement</h1>
        <p className="text-sm text-slate-500">Créé en brouillon : vérifiez-le, puis démarrez-le. Les échéances ne se créent qu'une fois la facturation démarrée.</p>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
      <Card className="p-4 space-y-4 min-w-0">
        <div className="space-y-1.5">
          <Label>Pharmacie cliente</Label>
          <PharmacyField value={pharmacy} onChange={setPharmacy} />
        </div>
        {offers.length === 0 ? (
          <p className="text-sm text-amber-700">Aucune offre publiée n'est en vente : publiez d'abord une offre (Offres).</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="billing-form-offer">Offre</Label>
                <Select value={offerId} onValueChange={setOfferId}>
                  <SelectTrigger id="billing-form-offer" className="h-10" data-testid="billing-form-offer"><SelectValue /></SelectTrigger>
                  <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="billing-form-periodicity">Périodicité</Label>
                <Select value={periodicity} onValueChange={(v) => { setPeriodicity(v); const lp = listPrice(version, v); setPrice(lp == null ? "" : minorToText(lp, currency)); }}>
                  <SelectTrigger id="billing-form-periodicity" className="h-10" data-testid="billing-form-periodicity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(version?.content.periods || []).map((p) => (
                      <SelectItem key={p.periodicity} value={p.periodicity}>{PERIODICITY_LABELS[p.periodicity]} · {priceText(p.price_minor, currency, p.periodicity)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="billing-form-price">Prix par période ({currency})</Label>
                <Input id="billing-form-price" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-form-price" />
                <p className="text-xs text-slate-500">{onQuote ? "Offre sur devis : saisissez le prix négocié." : `Prix de l'offre : ${money(listPrice(version, periodicity), currency)}.`}</p>
              </div>
              {reasonNeeded && (
                <div className="space-y-1.5">
                  <Label htmlFor="billing-form-reason">Motif du prix négocié</Label>
                  <Input id="billing-form-reason" value={priceReason} onChange={(e) => setPriceReason(e.target.value)} maxLength={500} className="h-10"
                    placeholder="Remise de lancement, réseau de pharmacies…" data-testid="billing-form-reason" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={trial} onCheckedChange={(v) => setTrial(!!v)} data-testid="billing-form-trial" />
              Commencer par une période d'essai gratuite{version?.content.trial_days ? ` (${version.content.trial_days} jours prévus par l'offre)` : ""}
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="billing-form-start">Début de l'abonnement</Label>
                <Input id="billing-form-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10" data-testid="billing-form-start" />
              </div>
              {trial && (
                <div className="space-y-1.5">
                  <Label htmlFor="billing-form-trial-end">Fin de l'essai (dernier jour gratuit)</Label>
                  <Input id="billing-form-trial-end" type="date" value={trialEnd} min={start} onChange={(e) => setTrialEnd(e.target.value)} className="h-10" data-testid="billing-form-trial-end" />
                  {trialDays && <p className="text-xs text-slate-500" data-testid="billing-form-trial-hint">Essai de {trialDays} jour{trialDays > 1 ? "s" : ""}. Facturation à partir du {formatDay(addDays(trialEnd, 1))}, une fois « Démarrer la facturation » fait.</p>}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-form-note">Note interne (facultatif)</Label>
              <Textarea id="billing-form-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} />
            </div>
          </>
        )}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="billing-form-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" asChild><Link to="/revenus/abonnements">Annuler</Link></Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="billing-form-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer le brouillon</Button>
        </div>
      </Card>
      <Card className="p-4 space-y-3 text-sm lg:sticky lg:top-4" data-testid="billing-form-summary">
        <h2 className="font-semibold text-slate-900">Récapitulatif</h2>
        <div>
          <Line label="Pharmacie">{pharmacy?.pharmacy_name || "—"}</Line>
          <Line label="Offre">{offer ? `${offer.name}${version ? ` (v${version.number})` : ""}` : "—"}</Line>
          <Line label="Prix">{priceMinor !== null && offer ? priceText(priceMinor, currency, periodicity) : "—"}</Line>
          {priceMinor !== null && offer && periodicity !== "monthly" && <Line label="Par mois">{money(monthlyMinor(priceMinor, periodicity), currency)}</Line>}
          <Line label="Début">{formatDay(start) || "—"}</Line>
          <Line label="Essai">{trial ? (trialDays ? `${trialDays} j, jusqu'au ${formatDay(trialEnd)}` : "date de fin à corriger") : "Sans essai"}</Line>
          <Line label="Facturé à partir du">{start ? formatDay(trial && trialEnd ? addDays(trialEnd, 1) : start) : "—"}</Line>
        </div>
        <p className="text-xs text-slate-500">Rien n'est facturé tant que l'abonnement reste en brouillon. Une échéance par période, payable dans le délai fixé dans Paramètres › Revenus.</p>
      </Card>
      </div>
    </div>
  );
}
